import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Email,
  Visibility,
  VisibilityOff,
  WhatsApp,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { requestFCMToken } from "../../services/firebaseConfig";
import { toast } from "react-toastify";
import axios from "axios";
import { useUser } from "../../provider/AuthProvider";
import { useTranslation } from "react-i18next";
import translateText from "../../services/google_translate";
import LangButton from "../../components/languagesButton";

const ResendTimer = ({ countdown }) => {
  const progress = ((60 - countdown) / 60) * 100;

  return (
    <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
      <Box position="relative" display="inline-flex">
        <CircularProgress
          variant="determinate"
          value={100}
          sx={{ color: "#e0e0e0" }}
        />
        <CircularProgress
          variant="determinate"
          value={progress}
          sx={{
            color: "#00b4d8",
            position: "absolute",
            left: 0,
          }}
        />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="caption" component="div" color="text.secondary">
            {countdown}s
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
const LoginPage = () => {
  const [loginData, setLoginData] = useState({ account: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fcmToken, setFcmToken] = useState("");
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendCountdown, setResendCountdown] = useState(3);
  const [resendEnabled, setResendEnabled] = useState(false);
  const { login } = useUser();
  const otpRefs = useRef([]);
  const [phone, setPhone] = useState(null);
  const [email, setEmail] = useState(null);
  const [verifyMethod, setVerifyMethod] = useState(null);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  useEffect(() => {
    let timer;
    if (step === 3 && resendCountdown > 0) {
      timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
    } else if (resendCountdown === 0) {
      setResendEnabled(true);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown, step]);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  };

  const maskEmail = (email) => {
    if (!email) return "";
    const [user, domain] = email.split("@");
    return user[0] + "***@" + domain;
  };

  const handleChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const fetchFCMToken = async () => {
      try {
        const token = await requestFCMToken();
        console.log(token);
        setFcmToken(token);
      } catch (error) {
        console.error("Error getting FCM token : ", error);
      }
    };
    fetchFCMToken();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      console.log(loginData);
      setLoading(true);
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/api/admin/signin",
        {
          username: loginData.account,
          password: loginData.password,
          fcm_token: fcmToken,
        },
        { withCredentials: true }
      );

      if (response.data.requires2FA) {
        setStep(2); // move to OTP step
        setPhone(response.data.phone);
        setEmail(response.data.email);
        // toast.info(t("otp_sent_to_your_whatsapp"));
      } else {
        finishLogin(response.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t("login_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (method) => {
    try {
      setVerifyMethod(method);
      const location = method === "whatsapp" ? phone : email;
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/send-otp`,
        {
          username: loginData.account,
          method,
          location,
        }
      );
      setStep(3); // Go to OTP input page
      toast.success(`OTP sent via ${method}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    }
  };

  const handleOTPVerify = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/verify-2fa`,
        {
          username: loginData.account,
          code: otp.join(""),
        },
        {
          withCredentials: true,
        }
      );
      toast.success(t("login_successful"));
      await login();
      navigate("/");
      // finishLogin(res.data.user);
    } catch (err) {
      toast.error(err.response?.data?.message || t("otp_is_not_valid"));
    } finally {
      setLoading(false);
    }
  };

  const finishLogin = async ({ user }) => {
    toast.success(t("login_successful"));
    await login(user);
    navigate("/");
  };

  const handleResend = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/resendOTP`,
        {
          username: loginData.account,
          method: verifyMethod,
        }
      );
      setResendCountdown(60);
      setResendEnabled(false);
      toast.success(`OTP sent via ${verifyMethod}`);
      // toast.success(t("otp_resent_to_whatsapp"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("failed_to_resend_otp"));
    }
  };

  const handleOTPChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOTPKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };
  const handleOTPPaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasteData)) {
      const digits = pasteData.split("");
      setOtp(digits);
      digits.forEach((digit, index) => {
        if (otpRefs.current[index]) {
          otpRefs.current[index].value = digit;
        }
      });
      otpRefs.current[5]?.focus();
      e.preventDefault(); // prevent default paste
    }
  };
  const maskPhoneNumber = (phone) => {
    if (!phone) return "";
    return phone.slice(0, 3) + "*****" + phone.slice(-2);
  };
  return (
    <Box>
      <Grid container rowSpacing={1} height="100vh">
        <Grid
          size={5}
          bgcolor="#0d1b2a"
          color="#cbd5e1"
          alignItems="center"
          justifyContent="center"
          display="flex"
        >
          <Box textAlign="center">
            <img
              src="/assets/icons/main-logo-blue.png"
              alt="Logo"
              style={{ width: 100, marginBottom: 50 }}
            />
            <Typography variant="h3" gutterBottom style={{ margin: "20px" }}>
              {t("welcome")}
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.7 }}
              style={{ margin: "20px" }}
            >
              {t("loginMessage")}
            </Typography>
            <LangButton />
            {/* <Box
              sx={{
                mt: 4,
                display: "flex",
                justifyContent: "center",
                fontSize: "1rem",
                fontWeight: 500,
                color: "#94a3b8",
                gap: 1,
              }}
            >
              <Button
                onClick={() => changeLanguage("ar")}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  color: currentLang === "en" ? "#00b4d8" : "#94a3b8",
                  textTransform: "none",
                  fontWeight: currentLang === "en" ? 600 : 400,
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#00b4d8",
                  },
                }}
              >
                {t("arabic")}
              </Button>
              <span>|</span>
              <Button
                onClick={() => changeLanguage("en")}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  color: currentLang === "ar" ? "#00b4d8" : "#94a3b8",
                  textTransform: "none",
                  fontWeight: currentLang === "ar" ? 600 : 400,
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#00b4d8",
                  },
                }}
              >
                {t("english")}
              </Button>
            </Box> */}
          </Box>
        </Grid>
        <Grid
          size={7}
          bgcolor="#12233a"
          color="#cbd5e1"
          alignItems="center"
          justifyContent="center"
          display="flex"
        >
          {step === 1 ? (
            <Container maxWidth="xs">
              <Typography
                variant="h4"
                align="center"
                gutterBottom
                sx={{ color: "#cbd5e1" }}
              >
                {t("sign_in")}
              </Typography>

              {error && (
                <Typography
                  variant="h7"
                  color="error"
                  align="center"
                  gutterBottom
                >
                  {error}
                </Typography>
              )}

              <Box component="form" onSubmit={handleLogin}>
                <TextField
                  margin="normal"
                  fullWidth
                  required
                  label={t("username")}
                  name="account"
                  value={loginData.account}
                  onChange={handleChange}
                  InputProps={{
                    style: { color: "#cbd5e1" },
                  }}
                  InputLabelProps={{
                    style: { color: "#90a4ae" },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#90a4ae", // Default border color
                      },
                      "&:hover fieldset": {
                        borderColor: "#00b4d8", // Border color on hover
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#00b4d8", // Border color when focused
                      },
                    },
                  }}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  required
                  label={t("password")}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={loginData.password}
                  onChange={handleChange}
                  InputProps={{
                    style: { color: "#cbd5e1" },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: "#90a4ae" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    style: { color: "#90a4ae" },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#90a4ae", // Default border color
                      },
                      "&:hover fieldset": {
                        borderColor: "#00b4d8", // Border color on hover
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#00b4d8", // Border color when focused
                      },
                    },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: "#00b4d8",
                    "&:hover": { backgroundColor: "#0096c7" },
                  }}
                >
                  <Typography variant="h6">
                    {loading ? t("sign_in") + "..." : t("sign_in")}
                  </Typography>
                </Button>

                <Divider sx={{ my: 2, borderColor: "#264653" }} />

                <Box textAlign="center" sx={{ color: "#90e0ef" }}>
                  {/* <Button
                  href="/signup"
                  sx={{ color: "#90e0ef", textTransform: "none" }}
                >
                  Sign up
                </Button>
                | */}
                  <Button
                    href="/remind-password"
                    sx={{ color: "#90e0ef", textTransform: "none" }}
                  >
                    {t("forgot_password")}
                  </Button>
                </Box>
              </Box>
            </Container>
          ) : step === 2 ? (
            <Container maxWidth="xs">
              <Typography variant="h6" align="center" gutterBottom>
                Please select how you would like to receive OTP.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                startIcon={<WhatsApp />}
                onClick={() => handleSendOtp("whatsapp")}
                sx={{ mt: 2, backgroundColor: "#25D366" }}
              >
                WhatsApp
              </Button>
              <Button
                fullWidth
                startIcon={<Email />}
                variant="contained"
                onClick={() => handleSendOtp("email")}
                sx={{ mt: 2, backgroundColor: "#007BFF" }}
              >
                Email
              </Button>
            </Container>
          ) : (
            <Container maxWidth="xs">
              <Box>
                <Box justifyContent="center" gap={1} mt={3} mb={2}>
                  <Typography variant="h6" sx={{ textAlign: "center", mb: 1 }}>
                    <WhatsApp fontSize="large" sx={{ mr: 1 }} color="success" />
                    {t("enter_the_6_digit_code_sent_via")}
                    {verifyMethod === "whatsapp"
                      ? ` ${maskPhoneNumber(phone)}`
                      : ` ${maskEmail(email)}`}
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="center"
                    gap={1}
                    mt={3}
                    mb={2}
                  >
                    {otp.map((value, index) => (
                      <TextField
                        key={index}
                        value={value}
                        onChange={(e) => handleOTPChange(index, e.target.value)}
                        onKeyDown={(e) => handleOTPKeyDown(index, e)}
                        onPaste={handleOTPPaste}
                        inputRef={(el) => (otpRefs.current[index] = el)}
                        inputProps={{
                          maxLength: 1,
                          style: { textAlign: "center", fontSize: "1.5rem" },
                        }}
                        sx={{ width: 45 }}
                      />
                    ))}
                  </Box>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleOTPVerify}
                  sx={{
                    mt: 3,
                    p: 1,
                    backgroundColor: "#00b4d8",
                    "&:hover": { backgroundColor: "#0096c7" },
                  }}
                >
                  {t("send")}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setStep(2);
                    setOtp(["", "", "", "", "", ""]);
                    setVerifyMethod(null);
                  }}
                  sx={{
                    mt: 3,
                    backgroundColor: "#8fb4d8",
                    "&:hover": { backgroundColor: "#0096c7" },
                  }}
                >
                  {"Back"}
                </Button>
                {!resendEnabled ? (
                  <ResendTimer countdown={resendCountdown} />
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    mt={2}
                  >
                    <Button
                      onClick={handleResend}
                      disabled={!resendEnabled}
                      sx={{ color: "#90e0ef", textTransform: "none" }}
                    >
                      {t("resend_code")}
                    </Button>
                  </Box>
                )}
              </Box>
            </Container>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoginPage;
